import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ClassSettingsSection, { type ClassSettingsDataChangeOptions } from './ClassSettingsSection';
import { usePaidPassGate } from '../contexts/PaidPassGateContext';

interface ClassSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId: string;
  onDataChange: (options?: ClassSettingsDataChangeOptions) => void;
}

const ClassSettingsModal: React.FC<ClassSettingsModalProps> = ({
  isOpen,
  onClose,
  workspaceId,
  onDataChange,
}) => {
  const { maxClasses } = usePaidPassGate();
  const [classStats, setClassStats] = useState(() => ({
    count: 0,
    max: maxClasses,
  }));

  const handleClassStatsChange = useCallback((stats: { count: number; max: number }) => {
    setClassStats(stats);
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setClassStats((prev) => ({
      ...prev,
      max: maxClasses,
    }));
  }, [isOpen, maxClasses]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="cp-class-settings-modal-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cp-class-settings-modal-title"
    >
      <div className="cp-class-settings-modal-dim" aria-hidden onClick={onClose} />
      <div
        className="cp-class-settings-modal-panel cp-class-register-settings-modal-panel"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cp-class-settings-modal-header">
          <div className="cp-class-settings-modal-heading">
            <h2 id="cp-class-settings-modal-title" className="cp-class-settings-modal-title">
              클래스 등록 및 설정
            </h2>
            <span
              className="cp-class-settings-modal-count"
              aria-label={`생성한 클래스 ${classStats.count}개, 최대 ${classStats.max}개`}
            >
              {classStats.count} / {classStats.max}
            </span>
          </div>
          <button
            type="button"
            className="cp-class-settings-modal-close"
            onClick={onClose}
            aria-label="닫기"
          >
            <img src="/modal-close.svg" alt="" width={18} height={18} />
          </button>
        </div>
        <div className="cp-class-settings-modal-body">
          <div className="cp-class-settings-modal-inner">
            <ClassSettingsSection
              workspaceId={workspaceId}
              onDataChange={onDataChange}
              onClassStatsChange={handleClassStatsChange}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ClassSettingsModal;
